import { SwissParties } from 'Constants/SwissParties'
import { group } from 'console'
import * as d3 from 'd3'
import { Dispatch, RefObject, SetStateAction } from 'react'
import curvedArrowR from 'Assets/curvedArrowR.svg'
import curvedArrowL from 'Assets/curvedArrowL.svg'

const width = document.documentElement.clientWidth
const circleRadius =
  width > 650 ? 5.5 : width > 550 ? 4.5 : width > 550 ? 3.5 : 3
const normalOpacity = 0.6
const tooltipDistanceToCursor = 10
export const appendHistogramm = (
  councilMembers: CouncilMember[],
  categoryToDisplay: keyof CouncilMember,
  groupToAppendTo: d3.Selection<SVGGElement, unknown, null, undefined>,
  xLinearScale: d3.ScaleLinear<number, number, never>,
  width: number,
  height: number,
  partyFilter: string[] | undefined,
  highlightCouncilMembers: string[] | undefined,
  setActiveCouncilMember: Dispatch<SetStateAction<CouncilMember | undefined>>,
  tooltipWrapperRef: RefObject<HTMLDivElement>
) => {
  function repositionToolTip(mouseX: number, mouseY: number) {
    const toolTipElement = d3
      .select(tooltipWrapperRef.current)
      .node() as HTMLElement
    const widthOfTooltip = toolTipElement?.clientWidth
    const heightOfTooltip = toolTipElement?.clientHeight
    const windowWidth = document.documentElement.clientWidth
    const windowHeight = document.documentElement.clientHeight

    const toolTipShouldBeLeft =
      widthOfTooltip + mouseX + tooltipDistanceToCursor > windowWidth
    const toolTipShouldBeTop =
      heightOfTooltip + mouseY + tooltipDistanceToCursor > windowHeight
    if (!toolTipShouldBeTop) {
      if (!toolTipShouldBeLeft) {
        d3.select(tooltipWrapperRef.current)
          .style('left', mouseX + tooltipDistanceToCursor + 'px')
          .style('top', mouseY + tooltipDistanceToCursor + 'px')
      } else {
        d3.select(tooltipWrapperRef.current)
          .style(
            'left',
            mouseX - widthOfTooltip - tooltipDistanceToCursor + 'px'
          )
          .style('top', mouseY + tooltipDistanceToCursor + 'px')
      }
    } else {
      if (!toolTipShouldBeLeft) {
        d3.select(tooltipWrapperRef.current)
          .style('left', mouseX + tooltipDistanceToCursor + 'px')
          .style(
            'top',
            mouseY - heightOfTooltip - tooltipDistanceToCursor + 'px'
          )
      } else {
        d3.select(tooltipWrapperRef.current)
          .style(
            'left',
            mouseX - widthOfTooltip - tooltipDistanceToCursor + 'px'
          )
          .style(
            'top',
            mouseY - heightOfTooltip - tooltipDistanceToCursor + 'px'
          )
      }
    }
  }
  const councilMembersWithCategory = councilMembers.filter(
    (d) => d[categoryToDisplay]
  )
  // find the leftmost council member of each party
  const leftMostCouncilMembers: CouncilMember[] = []

  Object.keys(SwissParties).forEach((party) => {
    const currentPartyMembers = councilMembersWithCategory.filter(
      (member) => member.party === party
    )
    const leftMostMember = currentPartyMembers.reduce((acc, currentMember) => {
      const currentMemberValue = currentMember[categoryToDisplay]?.[1]
      const accValue = acc[categoryToDisplay]?.[1]
      if (!currentMemberValue) {
        return acc
      }
      if (!accValue) {
        return currentMember
      }
      if (currentMemberValue < accValue) {
        return currentMember
      } else {
        return acc
      }
    }, currentPartyMembers[0])
    leftMostMember && leftMostCouncilMembers.push(leftMostMember)
    return leftMostCouncilMembers
  })
  // sort the parties from left to right by the leftmost council member
  const partiesFromLeftToRight = leftMostCouncilMembers
    .sort((a, b) => {
      const aValue = +a[categoryToDisplay]![1]
      const bValue = +b[categoryToDisplay]![1]
      if (!aValue || !bValue) {
        return 0
      }
      return aValue - bValue
    })
    .map((d) => d.party)

  var histogram = d3
    .bin()
    .value(function (d: any) {
      // console.log({d})
      return +d[categoryToDisplay]?.[1]
    }) // I need to give the vector of value
    .domain(xLinearScale.domain() as any) // then the domain of the graphic
    .thresholds(xLinearScale.ticks(55)) as any
  console.log(xLinearScale.ticks(70))
  console.log(xLinearScale.ticks(Math.round(width / (circleRadius * 2.6))))

  const bins = histogram(councilMembersWithCategory) as CouncilMember[][]

  // sort the bins so that members of the more left party(according to the most left member of the party) are on top
  bins.forEach((bin) =>
    bin.sort(
      (a, b) =>
        partiesFromLeftToRight.indexOf(a.party) -
        partiesFromLeftToRight.indexOf(b.party)
    )
  )
  setActiveCouncilMember(undefined)
  groupToAppendTo.selectAll('.trustIntervallLine').remove()

  bins
    .filter((bin: any) => bin.length > 0)
    .forEach((d: any, i: number) => {
      let k = 0
      for (let j = 0; j < d.length; j++) {
        const currentMember = d[j] as CouncilMember
        if (partyFilter !== undefined) {
          if (partyFilter.includes(currentMember.party)) {
            const yPositionOfMember =
              height - k * circleRadius * 2 - circleRadius
            k++
            const memberColor =
              SwissParties[currentMember.party as keyof typeof SwissParties]
                ?.color ?? SwissParties.andere.color
            if (highlightCouncilMembers === undefined) {
              highlightCouncilMembers = []
            }
            if (!highlightCouncilMembers.includes(currentMember.name)) {
              groupToAppendTo
                .append('rect')
                .attr('class', 'councilMemberCircle')
                .attr('x', xLinearScale(d.x0) - circleRadius)
                .attr('y', yPositionOfMember - circleRadius)
                .attr('width', circleRadius * 2)
                .attr('height', circleRadius * 2)
                .attr('rx', circleRadius / 2)
                .attr('ry', circleRadius)
                .style('fill', memberColor)
                .style('color', memberColor)
                .attr('data-member', currentMember.name)
                // .style('opacity', normalOpacity)

                .on('mouseover', function (this: any, event: MouseEvent) {
                  const tooltip = tooltipWrapperRef.current
                  if (tooltip) {
                    repositionToolTip(event.clientX, event.clientY)
                  }
                  // how do I get the council member here?
                  setActiveCouncilMember(currentMember)

                  // add trustintervall line to the histogram
                  const trustIntervallStart =
                    currentMember[categoryToDisplay]?.[0]
                  const trustIntervallEnd =
                    currentMember[categoryToDisplay]?.[2]
                  if (trustIntervallStart && trustIntervallEnd) {
                    groupToAppendTo
                      .append('line')
                      .attr('class', 'trustIntervallLine')
                      .attr('x1', xLinearScale(+trustIntervallStart))
                      .attr('x2', xLinearScale(+trustIntervallEnd))
                      .attr('y1', yPositionOfMember)
                      .attr('y2', yPositionOfMember)
                      .attr('color', memberColor)
                      .attr('stroke-width', 13)
                      .attr('opacity', 0.6)
                      .attr('stroke-linecap', 'round')
                  }

                  d3.select(this).raise()
                })
                .on('mouseout', function () {
                  setActiveCouncilMember(undefined)
                  groupToAppendTo.selectAll('.trustIntervallLine').remove()
                  //   d3.selectAll('.activeCouncilMemberCircle').raise()
                  //   d3.selectAll('.trustIntervallLine').raise()
                })
            } else {
              groupToAppendTo
                .append('rect')
                .attr('class', 'councilMemberCircle')
                .attr('class', 'activeCouncilMemberCircle')
                .attr('x', xLinearScale(d.x0) - circleRadius)
                .attr('y', yPositionOfMember - circleRadius)
                .attr('width', circleRadius * 2)
                .attr('height', circleRadius * 2)
                .attr('rx', circleRadius / 2)
                .attr('ry', circleRadius)
                .style('fill', memberColor)
                .style('color', memberColor)
                .attr('data-member', currentMember.name)

              // groupToAppendTo
              //   .append('image')
              //   .attr('class', 'annotationLine')
              //   .attr('xlink:href', curvedArrowR)
              //   .attr('x', xLinearScale(d.x0) + 20)
              //   .attr('y', yPositionOfMember - 70)
              //   .attr('width', 60)
              // .attr('transform', 'scale(-1, 1)')
            }
          }
        }

        // }
        // .on('mouseover', function (this: any, event: MouseEvent) {
        //   const tooltip = tooltipWrapperRef.current
        //   if (tooltip) {
        //     repositionToolTip(event.clientX, event.clientY)
        //   }
        //   // how do I get the council member here?
        //   setActiveCouncilMember(currentMember)

        //   // add trustintervall line to the histogram
        //   const trustIntervallStart = currentMember[categoryToDisplay]?.[0]
        //   const trustIntervallEnd = currentMember[categoryToDisplay]?.[2]
        //   if (trustIntervallStart && trustIntervallEnd) {
        //     groupToAppendTo
        //       .append('line')
        //       .attr('class', 'trustIntervallLine')
        //       .attr('x1', xLinearScale(+trustIntervallStart))
        //       .attr('x2', xLinearScale(+trustIntervallEnd))
        //       .attr('y1', yPositionOfMember)
        //       .attr('y2', yPositionOfMember)
        //       .attr('stroke-width', 4)
        //   }
        //   d3.select(this).raise()
        // })
        // .on('mouseout', function () {
        //   setActiveCouncilMember(undefined)
        //   groupToAppendTo.selectAll('.trustIntervallLine').remove()
        // })
      }
      groupToAppendTo.raise()
      d3.selectAll('.activeCouncilMemberCircle').raise()
      d3.selectAll('.trustIntervallLine').raise()
      d3.selectAll('.annotationLine').raise()
    })
}
